/**
 * Page Relevé : version responsive (écran) + version PDF officielle (hors-écran).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, Share2, BookOpen, AlertTriangle, Loader2, Lock,
  History, ChevronDown,
} from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi, ReleveData, ReleveMatiere, ReleveLeger } from '../services/portailApi';
import { ReleveLegerVue } from '../components/ReleveLeger';
import { ReclamationModal } from '../components/ReclamationModal';
import { AideLMD } from '../components/AideLMD';
import { ReleveOfficiel } from '../components/ReleveOfficiel';
import { ReleveResponsive } from '../components/ReleveResponsive';
import { RecapPedagogique } from '../components/RecapPedagogique';
import { ScanCopiesModal } from '../components/ScanCopiesModal';
import { genererPdfReleve } from '../services/pdfGenerator';

export default function Releve() {
  const navigate = useNavigate();
  const [semestre, setSemestre] = useState(1);
  const [data, setData] = useState<ReleveData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reclamationMatiere, setReclamationMatiere] = useState<ReleveMatiere | null>(null);
  const [scansMatiere, setScansMatiere] = useState<ReleveMatiere | null>(null);
  const [fenetreOuverte, setFenetreOuverte] = useState(false);
  const [aideOpen, setAideOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [codeVerif, setCodeVerif] = useState<string | null>(null);
  const [blocageMsg, setBlocageMsg] = useState<string | null>(null);
  const [blocageRaison, setBlocageRaison] = useState<string | null>(null);
  // Accès léger (2026-09-12) : décision sans notes pour les impayés « légers »
  const [leger, setLeger] = useState<ReleveLeger | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Consultation des années antérieures (2026-10-14). annee='' => année en cours.
  const [annee, setAnnee] = useState('');
  const [anneeCourante, setAnneeCourante] = useState('');
  const [anneesDispo, setAnneesDispo] = useState<{ annee: string; niveau: string; courante: boolean; a_resultats: boolean }[]>([]);
  const printRef = useRef<HTMLDivElement | null>(null);

  // Auto-dismiss du toast après 2.5s
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    portailApi.mesReclamations()
      .then((r) => setFenetreOuverte(r.fenetre.ouvertes))
      .catch(() => setFenetreOuverte(false));
  }, []);

  // Charge la liste des années où l'étudiant a des résultats (sélecteur historique).
  useEffect(() => {
    portailApi.annees()
      .then((r) => {
        setAnneesDispo(r.annees || []);
        if (r.courante) setAnneeCourante(r.courante);
      })
      .catch(() => { setAnneesDispo([]); });
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    setBlocageMsg(null);
    setBlocageRaison(null);
    setLeger(null);
    portailApi.releve(semestre, annee || undefined)
      .then((r) => { if (mounted) { setData(r); } })
      .catch((e: any) => {
        if (e.status === 401) {
          portailApi.clearToken();
          navigate('/portail/login', { replace: true });
        } else if (e.status === 423 && e.data?.notes_bloquees) {
          // Délibération en cours : on affiche la bannière sans rediriger
          if (mounted) {
            setBlocageMsg(e.data.message || e.data.detail || 'Consultation des notes temporairement suspendue');
            setBlocageRaison(e.data.raison || null);
            setData(null);
          }
        } else if (e.status === 403 && e.data?.releve_leger) {
          if (mounted) {
            setLeger(e.data.releve_leger as ReleveLeger);
            setData(null);
          }
        } else if (e.status === 403) {
          navigate('/portail/accueil', { replace: true });
        } else {
          if (mounted) setError(e.message || 'Erreur');
        }
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [semestre, annee, navigate]);

  useEffect(() => {
    portailApi.genererCodeVerification(semestre)
      .then((r) => setCodeVerif(r.code))
      .catch(() => setCodeVerif(null));
  }, [semestre]);

  const partager = async () => {
    if (!codeVerif || !data || data.examens_masques) return;
    const url = `${window.location.origin}/portail/verifier/${codeVerif}`;
    const text = `Relevé de notes ${data.etudiant.nom} ${data.etudiant.prenoms} (S${data.semestre}) — Code : ${codeVerif}\n${url}`;
    if ((navigator as any).share) {
      try { await (navigator as any).share({ title: `Relevé S${data.semestre} - 2i`, text, url }); } catch { /* annule */ }
    } else {
      try {
        await navigator.clipboard.writeText(text);
        setToast('Lien copié dans le presse-papiers');
      } catch {
        prompt('Copiez ce lien :', url);
      }
    }
  };

  const downloadPdf = async () => {
    if (!printRef.current || !data || data.examens_masques) return;
    setPdfLoading(true);
    try {
      const filename = `Releve_${data.etudiant.matricule}_S${data.semestre}.pdf`;
      await genererPdfReleve(printRef.current, filename);
    } catch (e: any) {
      alert("Erreur PDF : " + (e.message || ''));
    } finally {
      setPdfLoading(false);
    }
  };

  // Sélecteur d'année (consultation des années antérieures). Affiché seulement
  // si l'étudiant a plus d'une année de résultats (un L1 courant n'en a qu'une).
  const consulteArchive = !!annee && annee !== anneeCourante;
  const anneeSelector = anneesDispo.length > 1 ? (
    <div className={`rounded-xl border p-3 transition-colors ${
      consulteArchive
        ? 'bg-amber-950/20 border-amber-800/50'
        : 'bg-gray-900 border-gray-800'
    }`}>
      <div className="flex items-center gap-3">
        <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center border ${
          consulteArchive
            ? 'bg-amber-600/15 border-amber-700/40'
            : 'bg-blue-600/15 border-blue-700/40'
        }`}>
          <History size={18} className={consulteArchive ? 'text-amber-400' : 'text-blue-400'} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">
            Année académique
          </div>
          <div className="relative mt-0.5">
            <select
              value={annee || anneeCourante}
              onChange={(e) => { const v = e.target.value; setAnnee(v === anneeCourante ? '' : v); }}
              className="w-full appearance-none bg-transparent text-gray-100 text-sm font-semibold pr-7 focus:outline-none cursor-pointer"
              aria-label="Choisir l'année académique"
            >
              {anneesDispo.map((a) => (
                <option key={a.annee} value={a.annee} className="bg-gray-900 text-gray-100">
                  {a.annee} · {a.niveau}{a.courante ? ' · en cours' : ''}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              className={`absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none ${
                !consulteArchive && anneesDispo.filter((a) => !a.courante).length > 0
                  ? 'text-blue-400 animate-bounce'
                  : 'text-gray-500'
              }`}
            />
          </div>
          {/* Indice de découvrabilité : beaucoup d'étudiants ne savent pas que la
              liste déroulante donne accès aux années antérieures. On l'affiche
              uniquement sur l'année en cours quand il existe au moins une année
              précédente consultable. */}
          {!consulteArchive && anneesDispo.filter((a) => !a.courante).length > 0 && (
            <div className="mt-1 inline-flex items-center gap-1 text-[10.5px] font-medium text-blue-400">
              <History size={11} className="shrink-0" />
              Touchez ci-dessus pour voir vos résultats des années précédentes
            </div>
          )}
        </div>
        {consulteArchive && (
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full bg-amber-900/40 text-amber-300 border border-amber-700/50">
            Archives
          </span>
        )}
      </div>
    </div>
  ) : null;

  if (loading) {
    return (
      <PortailLayout showHeader title="Relevé de notes">
        <div className="text-center py-20">
          <Loader2 className="inline-block animate-spin h-10 w-10 text-blue-500" />
          <p className="text-gray-400 mt-4 text-sm">Chargement du relevé...</p>
        </div>
      </PortailLayout>
    );
  }

  // Accès léger (2026-09-12) : décision du semestre sans aucune note
  if (leger) {
    return (
      <PortailLayout showHeader title={`Résultat S${semestre}`} fullWidth>
        <div className="space-y-3 max-w-2xl mx-auto">
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-1 grid grid-cols-2 gap-1">
            {[1, 2].map((s) => (
              <button
                key={s}
                onClick={() => setSemestre(s)}
                className={`py-2 rounded text-sm font-medium transition-colors ${
                  semestre === s ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                Semestre {s}
              </button>
            ))}
          </div>
          <ReleveLegerVue data={leger} />
          <div className="text-center">
            <button
              onClick={() => navigate('/portail/accueil')}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded-lg"
            >
              <ArrowLeft size={14} /> Retour à l'accueil
            </button>
          </div>
        </div>
      </PortailLayout>
    );
  }

  // Fix 2026-09-07 : quand un semestre est bloqué, garder le sélecteur
  // semestre visible en tête pour permettre à l'étudiant de basculer vers
  // l'autre semestre — plus besoin de repasser par l'accueil.
  if (blocageMsg) {
    const semLabel = semestre === 1 ? 'impair (S1)' : 'pair (S2)';
    const autreSemestre = semestre === 1 ? 2 : 1;
    const autreLabel = autreSemestre === 1 ? 'impair (S1)' : 'pair (S2)';
    const annulee = blocageRaison === 'session_annulee';
    return (
      <PortailLayout showHeader title={`Relevé S${semestre}`} fullWidth>
        <div className="space-y-3">
          {anneeSelector}
          {/* Sélecteur semestre — visible même en blocage pour basculer */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-1 grid grid-cols-2 gap-1">
            {[1, 2].map((s) => (
              <button
                key={s}
                onClick={() => setSemestre(s)}
                className={`py-2 rounded text-sm font-medium transition-colors ${
                  semestre === s ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                Semestre {s}
              </button>
            ))}
          </div>

          <div className={`max-w-md mx-auto mt-4 rounded-xl p-6 text-center border ${
            annulee ? 'bg-red-950/30 border-red-800/60' : 'bg-amber-900/20 border-amber-700/50'
          }`}>
            <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full border mb-3 ${
              annulee ? 'bg-red-900/40 border-red-700' : 'bg-amber-900/40 border-amber-700'
            }`}>
              <Lock size={26} className={annulee ? 'text-red-300' : 'text-amber-300'} />
            </div>
            <h2 className={`text-base font-semibold mb-2 ${annulee ? 'text-red-100' : 'text-amber-100'}`}>
              {annulee
                ? `Session ordinaire annulée — semestre ${semLabel}`
                : `Semestre ${semLabel} temporairement suspendu`}
            </h2>
            <p className={`text-sm whitespace-pre-wrap leading-relaxed ${annulee ? 'text-red-200/90' : 'text-amber-200/90'}`}>
              {blocageMsg}
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              <button
                onClick={() => setSemestre(autreSemestre)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg"
              >
                Voir semestre {autreLabel}
              </button>
              <button
                onClick={() => navigate('/portail/accueil')}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded-lg"
              >
                <ArrowLeft size={14} /> Retour à l'accueil
              </button>
            </div>
          </div>
        </div>
      </PortailLayout>
    );
  }

  if (error || !data) {
    return (
      <PortailLayout showHeader title="Relevé de notes">
        <div className="max-w-md mx-auto space-y-3">
          {/* Fix 2026-09-07 : sélecteur semestre visible aussi en cas d'erreur */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-1 grid grid-cols-2 gap-1">
            {[1, 2].map((s) => (
              <button
                key={s}
                onClick={() => setSemestre(s)}
                className={`py-2 rounded text-sm font-medium transition-colors ${
                  semestre === s ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                Semestre {s}
              </button>
            ))}
          </div>
          <div className="bg-red-900/30 border border-red-800 rounded-lg p-4">
            <p className="text-red-300 text-sm">{error || 'Erreur de chargement'}</p>
            <button onClick={() => navigate('/portail/accueil')} className="mt-3 text-sm text-blue-400 hover:underline inline-flex items-center gap-1">
              <ArrowLeft size={14} /> Retour à l'accueil
            </button>
          </div>
        </div>
      </PortailLayout>
    );
  }

  const ues = data.ues || [];
  // Une matière n'est "à rattraper" QUE si :
  //  - le semestre n'est pas validé (sinon tout est OK par compensation)
  //  - ET l'UE qui la contient n'est pas validée par compensation
  //  - ET la moyenne ECUE est < 10
  const semestreValide = data.resultat_semestre?.valide ?? false;
  // Fix 2026-09-12 : notes d'examens masquées → ni décision, ni récapitulatif,
  // ni PDF, ni partage (le PDF et le code de vérification contournaient le masquage).
  const examensMasques = !!data.examens_masques;
  const matieresArattraper = semestreValide || examensMasques
    ? []
    : ues
        .filter((ue) => !ue.validee)
        .flatMap((ue) =>
          ue.matieres.filter((m) => !m.validee && (m.moyenne_ecue ?? 0) < 10)
        );

  return (
    <PortailLayout showHeader title={`Relevé S${data.semestre}`} fullWidth>
      {toast && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 bg-gray-900 border border-emerald-600/50 text-emerald-300 px-4 py-2 rounded-lg shadow-lg text-sm pointer-events-none">
          {toast}
        </div>
      )}
      <div className="space-y-3">
        {anneeSelector}
        {/* Sélecteur semestre */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-1 grid grid-cols-2 gap-1">
          {[1, 2].map((s) => (
            <button
              key={s}
              onClick={() => setSemestre(s)}
              className={`py-2 rounded text-sm font-medium transition-colors ${
                semestre === s ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              Semestre {s}
            </button>
          ))}
        </div>

        {/* Bandeau "examens masqués" (fix 2026-08-28) — affiché quand
            l'admin a activé le masquage des notes d'examens pour ce semestre.
            Seuls les devoirs restent visibles. */}
        {(data as any).examens_masques && (
          <div className="bg-amber-900/30 border-2 border-amber-700 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-amber-200">
                Notes d'examens temporairement masquées
              </div>
              <div className="text-xs text-amber-300 mt-1">
                {(data as any).examens_masques_message
                  || 'Les notes d\'examens ne sont pas encore consultables. Seules les notes de devoirs sont affichées pour l\'instant.'}
              </div>
            </div>
          </div>
        )}

        {/* Astuces (rubrique discrète) */}
        {data.astuces && data.astuces.length > 0 && (
          <details className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
            <summary className="px-4 py-2.5 text-sm font-medium text-gray-200 cursor-pointer hover:bg-gray-800 list-none flex items-center justify-between">
              <span>Conseils ({data.astuces.length})</span>
              <span className="text-xs text-gray-400">▼</span>
            </summary>
            <div className="border-t border-gray-800 p-3 space-y-2">
              {data.astuces.map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-gray-400 mt-0.5">•</span>
                  <span className={
                    a.type === 'success' ? 'text-green-400' :
                    a.type === 'warning' ? 'text-orange-400' :
                    a.type === 'danger' ? 'text-red-400' : 'text-blue-400'
                  }>{a.texte}</span>
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Stats classe (rubrique discrète) */}
        {data.stats_classe && (
          <details className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
            <summary className="px-4 py-2.5 text-sm font-medium text-gray-200 cursor-pointer hover:bg-gray-800 list-none flex items-center justify-between">
              <span>Comparaison classe ({data.stats_classe.effectif} étudiants)</span>
              <span className="text-xs text-gray-400">▼</span>
            </summary>
            <div className="border-t border-gray-800 p-3 grid grid-cols-3 gap-2 text-xs">
              <div>
                <div className="text-gray-400">Votre rang</div>
                <div className="font-semibold text-gray-100">
                  {data.stats_classe.position_estimee ?? '—'}/{data.stats_classe.effectif}
                </div>
              </div>
              <div>
                <div className="text-gray-400">Moy. classe</div>
                <div className="font-semibold text-gray-100">{data.stats_classe.moyenne_classe.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-gray-400">Médiane</div>
                <div className="font-semibold text-gray-100">{data.stats_classe.mediane.toFixed(2)}</div>
              </div>
            </div>
          </details>
        )}

        {/* Affichage RESPONSIVE du relevé */}
        <ReleveResponsive data={data} onVoirScans={(m) => setScansMatiere(m)} />

        {/* Récapitulatif pédagogique : matières à rattraper / compensation */}
        {!examensMasques && <RecapPedagogique data={data} />}

        {/* Réclamations rapides */}
        {fenetreOuverte && matieresArattraper.length > 0 && (
          <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-3">
            <div className="text-sm font-medium text-blue-300 mb-2 flex items-center gap-2">
              <AlertTriangle size={14} />
              Faire une réclamation
            </div>
            <div className="text-xs text-blue-400 mb-2">
              Cliquez sur une matière à rattraper pour déposer une réclamation :
            </div>
            <div className="flex flex-wrap gap-1.5">
              {matieresArattraper.map((m) => (
                <button
                  key={m.matiere_id}
                  onClick={() => setReclamationMatiere(m)}
                  className="text-xs px-2.5 py-1 bg-blue-900/30 hover:bg-blue-800/50 border border-blue-700 text-blue-200 rounded transition-colors"
                >
                  {m.code_matiere}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <button
            onClick={() => navigate('/portail/accueil')}
            className="py-2.5 text-sm bg-gray-900 border border-gray-700 hover:bg-gray-800 text-gray-200 rounded-lg font-medium inline-flex items-center justify-center gap-1.5"
          >
            <ArrowLeft size={14} />
            Accueil
          </button>
          <button
            onClick={() => setAideOpen(true)}
            className="py-2.5 text-sm bg-gray-900 border border-gray-700 hover:bg-gray-800 text-gray-200 rounded-lg font-medium inline-flex items-center justify-center gap-1.5"
          >
            <BookOpen size={14} />
            Aide LMD
          </button>
          {examensMasques ? (
            <div className="col-span-2 py-2.5 px-3 text-xs bg-gray-900 border border-gray-700 text-gray-400 rounded-lg inline-flex items-center justify-center gap-1.5 text-center">
              <Lock size={14} className="flex-shrink-0" />
              Téléchargement et partage disponibles après publication des notes d'examens
            </div>
          ) : (
            <>
              <button
                onClick={partager}
                disabled={!codeVerif}
                className="py-2.5 text-sm bg-gray-900 border border-gray-700 hover:bg-gray-800 text-gray-200 rounded-lg font-medium disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
              >
                <Share2 size={14} />
                Partager
              </button>
              <button
                onClick={downloadPdf}
                disabled={pdfLoading}
                className="py-2.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
              >
                {pdfLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                {pdfLoading ? 'Génération...' : 'Télécharger PDF'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Version PDF cachée hors-écran (pour la capture) — absente tant que les
          notes d'examens sont masquées */}
      {!examensMasques && (
        <div
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0,
            pointerEvents: 'none',
            opacity: 0,
          }}
          aria-hidden="true"
        >
          <div ref={printRef}>
            <ReleveOfficiel data={data} codeVerification={codeVerif} />
          </div>
        </div>
      )}

      {reclamationMatiere && (
        <ReclamationModal
          matiere={reclamationMatiere}
          semestre={semestre}
          onClose={() => setReclamationMatiere(null)}
          onSubmitted={() => {
            setReclamationMatiere(null);
            navigate('/portail/mes-reclamations');
          }}
        />
      )}
      {scansMatiere && (
        <ScanCopiesModal
          matiereId={scansMatiere.matiere_id}
          matiereNom={scansMatiere.nom_matiere}
          onClose={() => setScansMatiere(null)}
          onReclamation={(typeRec) => {
            // Pré-sélectionner le type lors de l'ouverture de la modal réclamation
            setScansMatiere(null);
            setReclamationMatiere(scansMatiere);
            // Le type devoir/examen est passé via state global ou via prop initiale
            // (la modal de réclamation laisse l'utilisateur choisir, type initial déjà 'examen')
          }}
        />
      )}
      {aideOpen && <AideLMD onClose={() => setAideOpen(false)} />}
    </PortailLayout>
  );
}
